import { scopeProjectRef, scopeThreadRef } from "@afar-research/client-runtime/environment";
import type {
  AvailableProjectWorktreesResult,
  EnvironmentId,
  ThreadId,
} from "@afar-research/contracts";
import {
  ChevronDownIcon,
  CloudIcon,
  FolderGit2Icon,
  FolderGitIcon,
  FolderIcon,
  MonitorIcon,
} from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";

import { useComposerDraftStore, type DraftId } from "../composerDraftStore";
import { useProject, useThread } from "../state/entities";
import { useEnvironmentQuery } from "../state/query";
import { worktreeEnvironment } from "../state/worktrees";
import { useIsMobile } from "../hooks/useMediaQuery";
import { resolveManagedWorktreeName } from "../lib/worktreeInventoryState";
import {
  type EnvMode,
  type EnvironmentOption,
  resolveCurrentWorkspaceLabel,
  resolveEnvModeLabel,
  resolveEffectiveEnvMode,
  resolveLockedWorkspaceLabel,
  shouldShowEnvironmentIdentity,
} from "./BranchToolbar.logic";
import { BranchToolbarBranchSelector } from "./BranchToolbarBranchSelector";
import { BranchToolbarEnvironmentSelector } from "./BranchToolbarEnvironmentSelector";
import { BranchToolbarEnvModeSelector } from "./BranchToolbarEnvModeSelector";
import {
  BranchToolbarExistingWorktreeDialog,
  resolveAvailableWorktreeName,
} from "./BranchToolbarExistingWorktreeDialog";
import { BranchToolbarWorktreeSelector } from "./BranchToolbarWorktreeSelector";
import { Button } from "./ui/button";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "./ui/menu";
import { Separator } from "./ui/separator";

const EMPTY_AVAILABLE_WORKTREES: AvailableProjectWorktreesResult["worktrees"] = [];

interface BranchToolbarProps {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  draftId?: DraftId;
  onEnvModeChange: (mode: EnvMode) => void;
  effectiveEnvModeOverride?: EnvMode;
  activeThreadBranchOverride?: string | null;
  onActiveThreadBranchOverrideChange?: (branch: string | null) => void;
  startFromOrigin: boolean;
  onStartFromOriginChange: (startFromOrigin: boolean) => void;
  envLocked: boolean;
  worktreeUnavailableReason?: string;
  onCheckoutPullRequestRequest?: (reference: string) => void;
  onComposerFocusRequest?: () => void;
  availableEnvironments?: readonly EnvironmentOption[];
  onEnvironmentChange?: (environmentId: EnvironmentId) => void;
}

interface MobileRunContextSelectorProps {
  envLocked: boolean;
  envModeLocked: boolean;
  environmentId: EnvironmentId;
  availableEnvironments: readonly EnvironmentOption[] | undefined;
  showEnvironmentIdentity: boolean;
  canChangeEnvironment: boolean;
  onEnvironmentChange: ((environmentId: EnvironmentId) => void) | undefined;
  effectiveEnvMode: EnvMode;
  activeWorktreePath: string | null;
  activeWorktreeName: string | null;
  onEnvModeChange: (mode: EnvMode) => void;
  availableWorktrees: AvailableProjectWorktreesResult["worktrees"];
  onExistingWorktreeRequest: () => void;
}

const MobileRunContextSelector = memo(function MobileRunContextSelector({
  envLocked,
  envModeLocked,
  environmentId,
  availableEnvironments,
  showEnvironmentIdentity,
  canChangeEnvironment,
  onEnvironmentChange,
  effectiveEnvMode,
  activeWorktreePath,
  activeWorktreeName,
  onEnvModeChange,
  availableWorktrees,
  onExistingWorktreeRequest,
}: MobileRunContextSelectorProps) {
  const activeEnvironment = useMemo(
    () => availableEnvironments?.find((env) => env.environmentId === environmentId) ?? null,
    [availableEnvironments, environmentId],
  );
  const activeWorktree = useMemo(
    () => availableWorktrees.find((worktree) => worktree.path === activeWorktreePath) ?? null,
    [activeWorktreePath, availableWorktrees],
  );
  const WorkspaceIcon =
    effectiveEnvMode === "worktree"
      ? FolderGit2Icon
      : activeWorktreePath
        ? FolderGitIcon
        : FolderIcon;
  const workspaceLabel = envModeLocked
    ? (activeWorktreeName ??
      (activeWorktree && resolveAvailableWorktreeName(activeWorktree)) ??
      resolveLockedWorkspaceLabel(activeWorktreePath))
    : effectiveEnvMode === "worktree"
      ? resolveEnvModeLabel("worktree")
      : ((activeWorktree && resolveAvailableWorktreeName(activeWorktree)) ??
        resolveCurrentWorkspaceLabel(activeWorktreePath));
  const isLocked = envLocked || envModeLocked;
  const EnvironmentIcon = activeEnvironment?.isPrimary ? MonitorIcon : CloudIcon;
  const icon = showEnvironmentIdentity ? (
    // Button's base styles apply `-mx-0.5` to descendant SVGs, which eats 4px
    // out of whatever gap we set. mx-0! cancels that so gap-0.5 reads as 2px.
    <span className="inline-flex shrink-0 items-center gap-0.5">
      <EnvironmentIcon className="size-3 shrink-0 mx-0!" />
      <WorkspaceIcon className="size-3 shrink-0 mx-0!" />
    </span>
  ) : (
    <WorkspaceIcon className="size-3 shrink-0" />
  );
  const triggerContent = (
    <>
      {icon}
      <span className="min-w-0 truncate">
        {showEnvironmentIdentity ? (activeEnvironment?.label ?? "Run on") : workspaceLabel}
      </span>
    </>
  );

  if (isLocked) {
    return (
      <span className="inline-flex min-w-0 max-w-[48%] flex-1 items-center justify-start gap-1 rounded-md border border-transparent px-[calc(--spacing(2)-1px)] text-sm font-medium text-muted-foreground/70 md:hidden">
        {triggerContent}
      </span>
    );
  }

  return (
    <Menu>
      <MenuTrigger
        render={<Button variant="ghost" size="xs" />}
        className="min-w-0 max-w-[48%] flex-1 justify-start text-muted-foreground/70 hover:text-foreground/80 md:hidden"
      >
        {triggerContent}
        <ChevronDownIcon className="size-3 shrink-0 opacity-50" />
      </MenuTrigger>
      <MenuPopup align="start" side="top" className="w-64">
        {canChangeEnvironment && availableEnvironments && onEnvironmentChange ? (
          <>
            <MenuGroup>
              <MenuGroupLabel>Run on</MenuGroupLabel>
              <MenuRadioGroup
                value={environmentId}
                onValueChange={(value) => onEnvironmentChange(value as EnvironmentId)}
              >
                {availableEnvironments.map((env) => {
                  const Icon = env.isPrimary ? MonitorIcon : CloudIcon;
                  return (
                    <MenuRadioItem
                      key={env.environmentId}
                      disabled={envLocked}
                      value={env.environmentId}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        <Icon className="size-3" />
                        <span className="min-w-0 truncate">{env.label}</span>
                      </span>
                    </MenuRadioItem>
                  );
                })}
              </MenuRadioGroup>
            </MenuGroup>
            <MenuSeparator />
          </>
        ) : null}
        <MenuGroup>
          <MenuGroupLabel>Workspace</MenuGroupLabel>
          <MenuRadioGroup
            value={activeWorktreePath ? "existing" : effectiveEnvMode}
            onValueChange={(value) => onEnvModeChange(value as EnvMode)}
          >
            <MenuRadioItem disabled={envModeLocked} value="local">
              <span className="flex min-w-0 items-center gap-1.5">
                {activeWorktreePath ? (
                  <FolderGitIcon className="size-3" />
                ) : (
                  <FolderIcon className="size-3" />
                )}
                <span className="min-w-0 truncate">
                  {resolveCurrentWorkspaceLabel(activeWorktreePath)}
                </span>
              </span>
            </MenuRadioItem>
            <MenuRadioItem disabled={envModeLocked} value="worktree">
              <span className="flex min-w-0 items-center gap-1.5">
                <FolderGit2Icon className="size-3" />
                <span className="min-w-0 truncate">{resolveEnvModeLabel("worktree")}</span>
              </span>
            </MenuRadioItem>
          </MenuRadioGroup>
          <MenuItem onClick={onExistingWorktreeRequest}>
            <span className="flex min-w-0 items-center gap-1.5">
              <FolderGitIcon className="size-3" />
              <span className="min-w-0 truncate">Existing worktree</span>
            </span>
          </MenuItem>
        </MenuGroup>
      </MenuPopup>
    </Menu>
  );
});

export const BranchToolbar = memo(function BranchToolbar({
  environmentId,
  threadId,
  draftId,
  onEnvModeChange,
  effectiveEnvModeOverride,
  activeThreadBranchOverride,
  onActiveThreadBranchOverrideChange,
  startFromOrigin,
  onStartFromOriginChange,
  envLocked,
  worktreeUnavailableReason,
  onCheckoutPullRequestRequest,
  onComposerFocusRequest,
  availableEnvironments,
  onEnvironmentChange,
}: BranchToolbarProps) {
  const threadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const serverThread = useThread(threadRef);
  const draftThread = useComposerDraftStore((store) =>
    draftId ? store.getDraftSession(draftId) : store.getDraftThreadByRef(threadRef),
  );
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);
  const [existingWorktreeDialogOpen, setExistingWorktreeDialogOpen] = useState(false);
  const activeProjectRef = serverThread
    ? scopeProjectRef(serverThread.environmentId, serverThread.projectId)
    : draftThread
      ? scopeProjectRef(draftThread.environmentId, draftThread.projectId)
      : null;
  const activeProject = useProject(activeProjectRef);
  const hasActiveThread = serverThread !== null || draftThread !== null;
  const activeWorktreePath = serverThread?.worktreePath ?? draftThread?.worktreePath ?? null;
  const effectiveEnvMode =
    effectiveEnvModeOverride ??
    resolveEffectiveEnvMode({
      activeWorktreePath,
      hasServerThread: serverThread !== null,
      draftThreadEnvMode: draftThread?.envMode,
    });
  const envModeLocked = envLocked || (serverThread !== null && activeWorktreePath !== null);
  const availableWorktreesQuery = useEnvironmentQuery(
    activeProject &&
      ((draftId && draftThread && !envModeLocked) ||
        (serverThread !== null && activeWorktreePath !== null))
      ? worktreeEnvironment.available({
          environmentId,
          input: { projectId: activeProject.id },
        })
      : null,
  );
  const availableWorktrees = availableWorktreesQuery.data?.worktrees ?? EMPTY_AVAILABLE_WORKTREES;
  const managedWorktreesQuery = useEnvironmentQuery(
    activeWorktreePath === null ? null : worktreeEnvironment.managed({ environmentId, input: {} }),
  );
  const activeWorktreeName = resolveManagedWorktreeName({
    worktrees: managedWorktreesQuery.data?.worktrees ?? [],
    threadId,
    worktreePath: activeWorktreePath,
  });
  const onExistingWorktreeChange = useCallback(
    (worktree: { readonly path: string; readonly branch: string | null }) => {
      if (!draftId || !draftThread) return;
      setDraftThreadContext(draftId, {
        envMode: "worktree",
        worktreePath: worktree.path,
        branch: worktree.branch,
      });
      onComposerFocusRequest?.();
    },
    [draftId, draftThread, onComposerFocusRequest, setDraftThreadContext],
  );
  const onWorkspaceModeChange = useCallback(
    (mode: EnvMode) => {
      if (mode === "worktree" && activeWorktreePath && draftId && draftThread) {
        setDraftThreadContext(draftId, {
          envMode: "worktree",
          worktreePath: null,
          branch: null,
        });
        onComposerFocusRequest?.();
        return;
      }
      onEnvModeChange(mode);
    },
    [
      activeWorktreePath,
      draftId,
      draftThread,
      onComposerFocusRequest,
      onEnvModeChange,
      setDraftThreadContext,
    ],
  );

  const canChangeEnvironment = Boolean(
    availableEnvironments && availableEnvironments.length > 1 && onEnvironmentChange,
  );
  const activeEnvironment = availableEnvironments?.find(
    (environment) => environment.environmentId === environmentId,
  );
  const showEnvironmentIdentity = shouldShowEnvironmentIdentity({
    activeEnvironment,
    canChangeEnvironment,
  });
  const isMobile = useIsMobile();

  if (!hasActiveThread || !activeProject) return null;

  return (
    <div className="mx-auto flex w-[calc(100%_-_2.5rem)] max-w-[calc(48rem_-_2.5rem)] items-center gap-2 rounded-b-2xl border border-t-0 border-border bg-muted/60 px-3 pb-1 pt-1">
      {isMobile ? (
        <MobileRunContextSelector
          envLocked={envLocked}
          envModeLocked={envModeLocked}
          environmentId={environmentId}
          availableEnvironments={availableEnvironments}
          showEnvironmentIdentity={showEnvironmentIdentity}
          canChangeEnvironment={canChangeEnvironment}
          onEnvironmentChange={onEnvironmentChange}
          effectiveEnvMode={effectiveEnvMode}
          activeWorktreePath={activeWorktreePath}
          activeWorktreeName={activeWorktreeName}
          onEnvModeChange={onWorkspaceModeChange}
          availableWorktrees={availableWorktrees}
          onExistingWorktreeRequest={() => setExistingWorktreeDialogOpen(true)}
        />
      ) : (
        <div className="flex min-w-0 shrink-0 items-center gap-1">
          {showEnvironmentIdentity && availableEnvironments && (
            <>
              <BranchToolbarEnvironmentSelector
                envLocked={envLocked || !canChangeEnvironment}
                environmentId={environmentId}
                availableEnvironments={availableEnvironments}
                {...(canChangeEnvironment && onEnvironmentChange ? { onEnvironmentChange } : {})}
              />
              <Separator orientation="vertical" className="mx-0.5 h-3.5!" />
            </>
          )}
          <BranchToolbarEnvModeSelector
            envLocked={envModeLocked}
            effectiveEnvMode={effectiveEnvMode}
            activeWorktreePath={activeWorktreePath}
            activeWorktreeName={activeWorktreeName}
            onEnvModeChange={onWorkspaceModeChange}
            availableWorktrees={availableWorktrees}
            onExistingWorktreeRequest={() => setExistingWorktreeDialogOpen(true)}
          />
        </div>
      )}

      <BranchToolbarBranchSelector
        className="min-w-0 flex-1 justify-end md:ml-auto md:flex-none"
        environmentId={environmentId}
        threadId={threadId}
        {...(draftId ? { draftId } : {})}
        envLocked={envLocked}
        {...(effectiveEnvModeOverride ? { effectiveEnvModeOverride } : {})}
        {...(activeThreadBranchOverride !== undefined ? { activeThreadBranchOverride } : {})}
        {...(onActiveThreadBranchOverrideChange ? { onActiveThreadBranchOverrideChange } : {})}
        startFromOrigin={startFromOrigin}
        onStartFromOriginChange={onStartFromOriginChange}
        {...(onCheckoutPullRequestRequest ? { onCheckoutPullRequestRequest } : {})}
        {...(onComposerFocusRequest ? { onComposerFocusRequest } : {})}
      />
      {effectiveEnvMode === "worktree" && draftId && draftThread ? (
        <BranchToolbarWorktreeSelector
          environmentId={environmentId}
          projectId={activeProject.id}
          draftTarget={draftId}
          draftThread={draftThread}
          disabled={envModeLocked}
        />
      ) : null}
      {draftId && draftThread ? (
        <BranchToolbarExistingWorktreeDialog
          open={existingWorktreeDialogOpen}
          onOpenChange={setExistingWorktreeDialogOpen}
          activeWorktreePath={activeWorktreePath}
          availableWorktrees={availableWorktrees}
          onConfirm={onExistingWorktreeChange}
        />
      ) : null}
      {worktreeUnavailableReason ? (
        <span className="truncate text-[11px] text-muted-foreground">
          {worktreeUnavailableReason}
        </span>
      ) : null}
    </div>
  );
});
